import Phaser from "phaser";
import { AssetKeys } from "../assets";
import { ENERGY_PICKUP_FEEDBACK_CONFIG } from "../energyPickupFeedbackConfig";
import type { PickupData } from "../types";

export type EnergyPickupVisual = {
  container: Phaser.GameObjects.Container;
  shadow: Phaser.GameObjects.Ellipse;
  halo: Phaser.GameObjects.Ellipse;
  ring: Phaser.GameObjects.Ellipse;
  icon: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;
};

export class EnergyPickupFeedbackSystem {
  private oneShots = new Set<Phaser.GameObjects.Sprite>();

  constructor(
    private scene: Phaser.Scene,
    private layers: {
      pickups: Phaser.GameObjects.Layer;
      particles: Phaser.GameObjects.Layer;
      actors: Phaser.GameObjects.Layer;
    }
  ) {}

  reset() {
    this.oneShots.forEach((sprite) => sprite.destroy());
    this.oneShots.clear();
  }

  createPickupVisual(pickup: PickupData): EnergyPickupVisual {
    const tint = pickup.tint ?? 0xb7ff00;
    const visualScale = pickup.visualScale ?? 1;
    const shadow = this.scene.add.ellipse(0, 18 * visualScale, 42 * visualScale, 12 * visualScale, 0x000000, 0.34);
    const halo = this.scene.add.ellipse(0, 0, 72 * visualScale, 72 * visualScale, tint, 0.12).setBlendMode(Phaser.BlendModes.ADD);
    const ring = this.scene.add.ellipse(0, 0, 60 * visualScale, 60 * visualScale, tint, 0.02).setStrokeStyle(2, tint, 0.42).setBlendMode(Phaser.BlendModes.ADD);
    const icon = this.createPickupIcon(pickup, tint, visualScale);
    const container = this.scene.add.container(pickup.x, pickup.y, [shadow, halo, ring, icon]);
    this.layers.pickups.add(container);
    return { container, shadow, halo, ring, icon };
  }

  updatePickupVisual(pickup: PickupData, visual: EnergyPickupVisual, elapsed: number) {
    const visualScale = pickup.visualScale ?? 1;
    const tint = pickup.tint ?? 0xb7ff00;
    visual.container.setPosition(pickup.x, pickup.y + Math.sin(elapsed * 4) * 6).setAlpha(1).setScale(1);
    if (visual.icon instanceof Phaser.GameObjects.Sprite && this.getIdleAnimationKey(pickup)) {
      const scale = this.getIdleScale(pickup);
      visual.icon.setTint(tint).setDisplaySize(512 * scale, 512 * scale);
    } else {
      visual.icon.setTint(tint).setDisplaySize(48 * visualScale, 48 * visualScale);
    }
    visual.halo.setFillStyle(tint, visual.halo.fillAlpha);
    visual.ring.setStrokeStyle(2, tint, visual.ring.alpha);
  }

  playPickupFeedback(pickup: PickupData, wolfPosition: { x: number; y: number }) {
    void pickup;
    void wolfPosition;
  }

  private createPickupIcon(pickup: PickupData, tint: number, visualScale: number) {
    return this.scene.add.image(0, -2, AssetKeys.vfx.aimBooster).setDisplaySize(48 * visualScale, 48 * visualScale).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);
  }

  private playOneShot(animationKey: string, x: number, y: number, scale: number, layer: Phaser.GameObjects.Layer) {
    if (!this.scene.anims.exists(animationKey)) return;
    const sprite = this.scene.add.sprite(x, y, ENERGY_PICKUP_FEEDBACK_CONFIG.assets.wolfPickupFlash.key);
    sprite.setDisplaySize(512 * scale, 512 * scale).setBlendMode(Phaser.BlendModes.ADD);
    layer.add(sprite);
    this.oneShots.add(sprite);
    sprite.play(animationKey);
    sprite.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.oneShots.delete(sprite);
      sprite.destroy();
    });
  }

  private getIdleAnimationKey(pickup: PickupData) {
    if (pickup.syncType === "energy_trail" || pickup.syncType === "soft_energy_orb") return "energy_trail_orb";
    if (pickup.syncType === "champion_shard") return "champion_shard_idle";
    if (pickup.syncType === "pressure_core" || pickup.syncType === "spirit_energy") return "pressure_core_idle";
    if (pickup.syncType === "pack_echo_core" || pickup.syncType === "final_echo") return "pack_echo_core_idle";
    if (this.isChampionPickup(pickup)) return "champion_core_idle";
    return undefined;
  }

  private getIdleScale(pickup: PickupData) {
    if (pickup.syncType === "energy_trail" || pickup.syncType === "soft_energy_orb") return ENERGY_PICKUP_FEEDBACK_CONFIG.scale.energyTrailOrb;
    if (pickup.syncType === "champion_shard") return ENERGY_PICKUP_FEEDBACK_CONFIG.scale.championShard;
    if (pickup.syncType === "pressure_core" || pickup.syncType === "spirit_energy") return ENERGY_PICKUP_FEEDBACK_CONFIG.scale.pressureCore;
    if (pickup.syncType === "pack_echo_core" || pickup.syncType === "final_echo") return ENERGY_PICKUP_FEEDBACK_CONFIG.scale.packEchoCore;
    return ENERGY_PICKUP_FEEDBACK_CONFIG.scale.championCore;
  }

  private isChampionPickup(pickup: PickupData) {
    return (
      pickup.syncType === "champion_core" ||
      pickup.syncType === "champion_burst" ||
      pickup.syncType === "first_boost_core" ||
      pickup.syncType === "final_champion_core"
    );
  }
}
